import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ApiService } from 'src/app/services/api.service';
import swal from 'sweetalert2';

@Component({
  selector: 'app-salon-pos',
  templateUrl: './salon-pos.component.html',
  styleUrls: ['./salon-pos.component.css']
})
export class SalonPosComponent {
  @Input() sucId = 1;
  @Input() servicios: any[] = [];
  @Input() productos: any[] = [];
  @Input() trabajadores: any[] = [];
  @Input() metodosPago: any[] = [];
  @Input() userRole = '';
  @Input() clientes: any[] = [];
  @Output() changed = new EventEmitter<void>();

  get activeWorkers(): any[] {
    return (this.trabajadores || []).filter(w => Number(w.tra_activo) === 1);
  }

  mode = 'SERVICIO';
  serviceCategoryFilter = 'TODOS';
  selectedWorker = 0;
  selectedWorkers: number[] = [];
  selectedSeller = 0;
  selectedPayment = 1;
  cashOpening = 100;
  caja: any = null;
  cajaBloqueo = 0;
  busy = false;
  cart: any[] = [];
  atenciones: any[] = [];
  activeAttention: any = null;
  attentionFilter = '';
  today = this.toDateInput(new Date());
  pagos: any[] = [{ mpa_id: 1, monto: 0, referencia: '' }];
  cliente: any = {
    per_tipdoc: 'DNI',
    per_numdoc: '',
    per_apepat: '',
    per_apemat: '',
    per_nombres: '',
    per_celular: '',
    per_correo: ''
  };

  constructor(private api: ApiService) { }

  ngOnChanges(): void {
    this.loadCajaEstado();
    this.loadAtenciones();
  }

  get total(): number {
    return this.cart.reduce((sum, item) => sum + Number(item.total || 0), 0);
  }

  get catalogItems(): any[] {
    if (this.mode !== 'SERVICIO') {
      return this.productos;
    }

    if (this.serviceCategoryFilter === 'TODOS') {
      return this.servicios;
    }

    return this.servicios.filter(item => this.categoryKey(item.csv_nombre) === this.serviceCategoryFilter);
  }

  get serviceCategories(): string[] {
    const categories = this.servicios
      .map(item => String(item.csv_nombre || '').trim())
      .filter(category => !!category);

    return Array.from(new Set(categories)).sort((a, b) => a.localeCompare(b));
  }

  get pagosTotal(): number {
    return this.pagos.reduce((sum, item) => sum + Number(item.monto || 0), 0);
  }

  get pagoPendiente(): number {
    return Math.max(0, Number((this.total - this.pagosTotal).toFixed(2)));
  }

  get hasOpenCash(): boolean {
    return !!this.caja && this.caja.caj_estado === 'ABIERTA';
  }

  get canManageCash(): boolean {
    return ['ADMINISTRADOR', 'CAJERO'].indexOf(String(this.userRole || '').toUpperCase()) >= 0;
  }

  get canCharge(): boolean {
    return this.canManageCash;
  }

  get clientLabel(): string {
    if (this.activeAttention) {
      return this.activeAttention.cliente || this.activeAttention.atn_codigo || 'Persona seleccionada';
    }
    const name = `${this.cliente.per_apepat || ''} ${this.cliente.per_apemat || ''} ${this.cliente.per_nombres || ''}`.trim();
    return name || this.cliente.per_numdoc || this.cliente.per_celular || 'Registrar persona';
  }

  itemName(item: any): string {
    return this.mode === 'SERVICIO' ? item.ser_nombre : item.pro_nombre;
  }

  itemPrice(item: any): number {
    return Number(this.mode === 'SERVICIO' ? item.ser_precio : item.pro_precio_venta);
  }

  categoryKey(category: string): string {
    return String(category || '').trim().toUpperCase();
  }

  setMode(mode: string): void {
    this.mode = mode;
    if (mode !== 'SERVICIO') {
      this.serviceCategoryFilter = 'TODOS';
    }
  }

  setServiceCategory(category: string): void {
    this.serviceCategoryFilter = category;
  }

  addExtra(): void {
    const workerIds = this.getServiceWorkerIds();
    if (!workerIds.length) {
      swal.fire('Responsable requerido', 'Selecciona una o mas trabajadoras para el extra.', 'warning');
      return;
    }

    const extra = this.servicios.find(item => String(item.ser_nombre || '').toLowerCase() === 'extra de servicio');
    if (!extra) {
      swal.fire('Extra no configurado', 'No se encontro el servicio Extra de servicio en el catalogo.', 'error');
      return;
    }

    swal.fire({
      title: 'Agregar extra',
      html: '<input id="extraConcepto" class="swal2-input" style="text-transform: uppercase;" placeholder="Detalle del extra"><input id="extraMonto" type="number" min="0" class="swal2-input" placeholder="Monto">',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Agregar',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        const concepto = ((document.getElementById('extraConcepto') as HTMLInputElement).value || '').toUpperCase();
        const monto = Number((document.getElementById('extraMonto') as HTMLInputElement).value || 0);
        if (!concepto || monto <= 0) {
          swal.showValidationMessage('Ingresa concepto y monto mayor a cero');
          return false;
        }
        return { concepto: concepto, monto: monto };
      }
    }).then((res: any) => {
      if (!res.isConfirmed || !res.value) {
        return;
      }
      const name = `Extra: ${res.value.concepto}`;
      this.addServiceRow(extra.ser_id, name, Number(res.value.monto), workerIds);
    });
  }

  addItem(item: any): void {
    const tipo = this.mode;
    const itemId = tipo === 'SERVICIO' ? item.ser_id : item.pro_id;
    const name = this.itemName(item);
    const price = this.itemPrice(item);

    if (tipo === 'SERVICIO') {
      const workerIds = this.getServiceWorkerIds();
      if (!workerIds.length) {
        swal.fire('Trabajadora requerida', 'Selecciona una o mas trabajadoras antes de tocar el servicio.', 'warning');
        return;
      }
      this.addServiceRow(itemId, name, price, workerIds);
      return;
    }

    const workerId = this.selectedSeller;
    if (!workerId) {
      swal.fire('Vendedor requerido', 'Selecciona el vendedor antes de agregar el producto.', 'warning');
      return;
    }
    const found = this.cart.find(row => row.tipo === tipo && row.item_id === itemId && row.tra_id === workerId);
    if (found) {
      found.cantidad = Number(found.cantidad) + 1;
      found.total = Number(found.cantidad) * Number(found.precio);
      return;
    }

    this.cart.push({
      tipo: tipo,
      item_id: itemId,
      nombre: name,
      tra_id: workerId,
      trabajador: this.workerName(workerId),
      cantidad: 1,
      precio: price,
      total: price
    });
  }

  toggleWorker(workerId: number): void {
    const id = Number(workerId);
    const index = this.selectedWorkers.indexOf(id);
    if (index >= 0) {
      this.selectedWorkers.splice(index, 1);
      return;
    }
    this.selectedWorkers.push(id);
  }

  isWorkerSelected(workerId: number): boolean {
    return this.selectedWorkers.indexOf(Number(workerId)) >= 0;
  }

  changeQuantity(item: any, step: number): void {
    item.cantidad = Math.max(1, Number(item.cantidad) + step);
    this.recalculateItem(item);
  }

  changePrice(item: any, value: any): void {
    item.precio = Math.max(0, Number(value || 0));
    this.recalculateItem(item);
  }

  removeItem(index: number): void {
    this.cart.splice(index, 1);
  }

  clearCart(): void {
    this.cart = [];
  }

  clearClient(): void {
    this.activeAttention = null;
    this.cliente = {
      per_tipdoc: 'DNI',
      per_numdoc: '',
      per_apepat: '',
      per_apemat: '',
      per_nombres: '',
      per_celular: '',
      per_correo: ''
    };
  }

  onDocumentChange(val: string): void {
    const doc = String(val || '').trim();
    if (!doc) {
      return;
    }
    const found = (this.clientes || []).find(c => String(c.per_numdoc || '').trim() === doc);
    if (found) {
      this.applyFoundClient(found);
      return;
    }
    if (doc.length >= 8) {
      this.searchClientByDocument(doc);
    }
  }

  onDocumentBlur(): void {
    const doc = String(this.cliente.per_numdoc || '').trim();
    if (!doc) {
      return;
    }
    const found = (this.clientes || []).find(c => String(c.per_numdoc || '').trim() === doc);
    if (found) {
      this.applyFoundClient(found);
      return;
    }
    this.searchClientByDocument(doc);
  }

  private searchClientByDocument(doc: string): void {
    if (!doc) {
      return;
    }
    this.api.salonClienteLis({ p_buscar: doc, p_activo: 1 }).subscribe((data: any) => {
      const rows = Array.isArray(data) ? (Array.isArray(data[0]) ? data[0] : data) : [];
      const match = rows.find((c: any) => String(c.per_numdoc || '').trim() === doc) || (rows.length === 1 ? rows[0] : null);
      if (match) {
        this.applyFoundClient(match);
      }
    });
  }

  private applyFoundClient(match: any): void {
    this.cliente.per_tipdoc = match.per_tipdoc || 'DNI';
    this.cliente.per_numdoc = match.per_numdoc || this.cliente.per_numdoc;
    this.cliente.per_apepat = match.per_apepat || '';
    this.cliente.per_apemat = match.per_apemat || '';
    this.cliente.per_nombres = match.per_nombres || '';
    this.cliente.per_celular = match.per_celular || '';
    this.cliente.per_correo = match.per_correo || '';
  }

  saveClientOnly(): void {
    if (!this.hasClientData()) {
      swal.fire('Datos del cliente', 'Ingresa al menos nombres, documento o celular para guardar.', 'warning');
      return;
    }

    const doc = String(this.cliente.per_numdoc || '').trim();
    if (doc) {
      const existing = (this.clientes || []).find((c: any) => String(c.per_numdoc || '').trim() === doc);
      if (existing && existing.cli_id) {
        const nombre = `${existing.per_apepat || ''} ${existing.per_apemat || ''} ${existing.per_nombres || ''}`.trim();
        swal.fire({
          title: 'Cliente ya registrado',
          text: `El documento ${doc} ya pertenece a "${nombre}".`,
          icon: 'info'
        });
        return;
      }
    }

    this.busy = true;
    this.api.salonClienteGra({
      p_cli_id: 0,
      p_per_tipdoc: this.cliente.per_tipdoc || 'DNI',
      p_per_numdoc: this.cliente.per_numdoc || '',
      p_per_apepat: this.cliente.per_apepat || '',
      p_per_apemat: this.cliente.per_apemat || '',
      p_per_nombres: this.cliente.per_nombres || (this.cliente.per_numdoc ? `CLIENTE ${this.cliente.per_numdoc}` : 'CLIENTE'),
      p_per_celular: this.cliente.per_celular || '',
      p_per_correo: this.cliente.per_correo || ''
    }).subscribe(() => {
      this.busy = false;
      swal.fire('Listo', 'Cliente guardado correctamente sin generar venta ni turno.', 'success');
      this.changed.emit();
    }, (err) => this.showError(err, 'No se pudo guardar el cliente'));
  }

  registerLobby(): void {
    if (!this.hasClientData()) {
      swal.fire('Datos de la persona', 'Ingresa al menos nombre, documento o celular para registrarla en lobby.', 'warning');
      return;
    }

    this.busy = true;
    this.saveClientIfNeeded((cliId: number) => this.api.salonAtencionReg({
      p_suc_id: this.sucId,
      p_cli_id: cliId,
      p_observacion: 'Persona registrada en lobby',
      p_servicios: []
    }).subscribe((data: any) => {
      this.busy = false;
      const row = Array.isArray(data) ? data[0] : data;
      this.activeAttention = Object.assign({}, row, {
        cliente: this.clientLabel,
        cli_id: cliId,
        servicios: 'Sin servicios asignados',
        total: 0
      });
      swal.fire('Persona en lobby', 'Ahora selecciona sus servicios y una o mas trabajadoras.', 'success');
      this.clearCart();
      this.loadAtenciones();
      this.changed.emit();
    }, (err) => this.showError(err, 'No se pudo registrar la persona en lobby')));
  }

  openCash(): void {
    this.busy = true;
    this.api.salonCajaAbrir({
      p_suc_id: this.sucId,
      p_fecha: this.today,
      p_monto: this.cashOpening
    }).subscribe(() => {
      this.busy = false;
      swal.fire('Caja abierta', 'Ya puedes registrar ventas del dia.', 'success');
      this.loadCajaEstado();
      this.changed.emit();
    }, (err) => this.showError(err, 'No se pudo abrir caja'));
  }

  loadCajaEstado(): void {
    this.api.salonCajaEstado({
      p_suc_id: this.sucId,
      p_fecha: this.today
    }).subscribe((data: any) => {
      const row = Array.isArray(data) ? (data[0] || null) : null;
      this.cajaBloqueo = row ? Number(row.cajas_cerradas_sin_conformidad || 0) : 0;
      this.caja = row && row.caj_id ? row : null;
    });
  }

  addPago(): void {
    const defaultMpa = (this.pagos && this.pagos.length) ? (this.pagos[this.pagos.length - 1].mpa_id || 1) : 1;
    this.pagos.push({ mpa_id: Number(defaultMpa), monto: this.pagoPendiente, referencia: '' });
  }

  removePago(index: number): void {
    if (this.pagos.length === 1) {
      this.pagos[0].monto = this.total;
      this.pagos[0].mpa_id = Number(this.pagos[0].mpa_id || 1);
      return;
    }
    this.pagos.splice(index, 1);
  }

  fillSinglePayment(): void {
    if (!this.pagos || !this.pagos.length) {
      this.pagos = [{ mpa_id: Number(this.selectedPayment || 1), monto: this.total, referencia: '' }];
      return;
    }

    if (this.pagos.length === 1) {
      this.pagos[0].monto = this.total;
      this.pagos[0].mpa_id = Number(this.pagos[0].mpa_id || this.selectedPayment || 1);
      this.selectedPayment = this.pagos[0].mpa_id;
      return;
    }

    if (this.pagoPendiente > 0) {
      const last = this.pagos[this.pagos.length - 1];
      last.monto = Number((Number(last.monto || 0) + this.pagoPendiente).toFixed(2));
      last.mpa_id = Number(last.mpa_id || 1);
      return;
    }

    const currentMpaId = Number(this.pagos[0].mpa_id || this.selectedPayment || 1);
    this.selectedPayment = currentMpaId;
    this.pagos = [{ mpa_id: currentMpaId, monto: this.total, referencia: '' }];
  }

  validPayments(): any[] {
    return this.pagos
      .filter(item => Number(item.mpa_id) > 0 && Number(item.monto) > 0)
      .map(item => ({
        mpa_id: Number(item.mpa_id),
        monto: Number(item.monto),
        referencia: item.referencia || ''
      }));
  }

  loadAtenciones(): void {
    this.api.salonAtencionLis({
      p_suc_id: this.sucId,
      p_fecha: this.today,
      p_estado: this.attentionFilter
    }).subscribe((data: any) => {
      this.atenciones = Array.isArray(data)
        ? data.filter(item => item.atn_estado !== 'ANULADO' && (this.attentionFilter || item.atn_estado !== 'PAGADO'))
        : [];
    });
  }

  registerAttention(): void {
    if (!this.hasOpenCash) {
      swal.fire('Caja requerida', 'Primero debes aperturar caja para registrar atenciones del dia.', 'warning');
      return;
    }

    const servicios = this.cart.filter(item => item.tipo === 'SERVICIO');
    const productos = this.cart.filter(item => item.tipo === 'PRODUCTO');
    if (!servicios.length) {
      swal.fire('Servicios requeridos', 'Agrega al menos un servicio para registrar una atencion.', 'info');
      return;
    }
    if (productos.length) {
      swal.fire('Separa la venta', 'Los productos deben cobrarse como venta directa para descontar stock correctamente.', 'warning');
      return;
    }

    if (!this.activeAttention || !this.activeAttention.atn_id) {
      swal.fire('Persona requerida', 'Primero registra o selecciona una persona del lobby.', 'warning');
      return;
    }

    this.busy = true;
    this.api.salonAtencionServiciosAgregar({
      p_atn_id: this.activeAttention.atn_id,
      p_servicios: this.backendItems(servicios)
    }).subscribe(() => {
      this.busy = false;
      swal.fire('Servicios asignados', 'La persona sigue en lobby hasta iniciar/finalizar/cobrar.', 'success');
      this.clearCart();
      this.loadAtenciones();
      this.changed.emit();
    }, (err) => this.showError(err, 'No se pudo asignar servicios'));
  }

  selectAttention(attention: any): void {
    this.activeAttention = attention;
    this.clearCart();
  }

  updateAttention(attention: any, estado: string): void {
    if (estado === 'ANULADO') {
      this.cancelAttention(attention);
      return;
    }

    this.busy = true;
    this.api.salonAtencionEstadoUpd({
      p_atn_id: attention.atn_id,
      p_estado: estado
    }).subscribe(() => {
      this.busy = false;
      this.loadAtenciones();
      this.changed.emit();
    }, (err) => this.showError(err, 'No se pudo cambiar el estado'));
  }

  cancelAttention(attention: any): void {
    const hasAmount = Number(attention.total || 0) > 0;
    swal.fire({
      title: hasAmount ? 'Anular atencion' : 'Quitar del lobby',
      text: hasAmount
        ? 'La atencion tiene servicios asignados. Se marcara como anulada y no podra cobrarse.'
        : 'La persona saldra del lobby porque no consumio nada.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: hasAmount ? 'Anular' : 'Quitar',
      cancelButtonText: 'Cancelar'
    }).then((res: any) => {
      if (!res.isConfirmed) {
        return;
      }
      this.busy = true;
      this.api.salonAtencionEstadoUpd({
        p_atn_id: attention.atn_id,
        p_estado: 'ANULADO'
      }).subscribe(() => {
        this.busy = false;
        if (this.activeAttention && Number(this.activeAttention.atn_id) === Number(attention.atn_id)) {
          this.activeAttention = null;
        }
        swal.fire('Listo', hasAmount ? 'La atencion fue anulada.' : 'La persona fue retirada del lobby.', 'success');
        this.loadAtenciones();
        this.changed.emit();
      }, (err) => this.showError(err, 'No se pudo anular la atencion'));
    });
  }

  payAttention(attention: any): void {
    if (!this.hasOpenCash) {
      swal.fire('Caja requerida', 'Primero debes aperturar caja para cobrar una atencion.', 'warning');
      return;
    }

    if (this.pagosTotal === 0) {
      const currentMpaId = Number((this.pagos && this.pagos.length && this.pagos[0].mpa_id) ? this.pagos[0].mpa_id : (this.selectedPayment || 1));
      this.pagos = [{ mpa_id: currentMpaId, monto: Number(attention.total || 0), referencia: '' }];
    }
    const pagos = this.validPayments();
    if (Number(this.pagosTotal.toFixed(2)) !== Number(Number(attention.total || 0).toFixed(2))) {
      swal.fire('Pago incompleto', 'La suma de pagos debe coincidir con el total de la atencion.', 'warning');
      return;
    }
    this.busy = true;
    this.api.salonAtencionPagar({
      p_atn_id: attention.atn_id,
      p_pagos: pagos
    }).subscribe(() => {
      this.busy = false;
      swal.fire('Atencion cobrada', `Ingreso a caja: S/ ${Number(attention.total || 0).toFixed(2)}`, 'success');
      this.pagos = [{ mpa_id: this.selectedPayment || 1, monto: 0, referencia: '' }];
      this.loadCajaEstado();
      this.loadAtenciones();
      this.activeAttention = null;
      this.changed.emit();
    }, (err) => this.showError(err, 'No se pudo cobrar la atencion'));
  }

  closeCash(): void {
    swal.fire({
      title: 'Cerrar caja',
      text: 'Se calcularan ingresos, egresos y saldo final. Luego deberas dar conformidad.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Cerrar caja',
      cancelButtonText: 'Cancelar'
    }).then((res: any) => {
      if (!res.isConfirmed) {
        return;
      }
      this.busy = true;
      this.api.salonCajaCerrar({
        p_suc_id: this.sucId,
        p_fecha: this.caja && this.caja.caj_fecha ? this.caja.caj_fecha : this.today
      }).subscribe((data: any) => {
        this.busy = false;
        const row = Array.isArray(data) ? data[0] : data;
        const cierre = row && row.caj_cierre ? Number(row.caj_cierre).toFixed(2) : '0.00';
        swal.fire('Caja cerrada', `Cierre calculado: S/ ${cierre}. Falta conformidad para poder abrir la proxima caja.`, 'success');
        this.loadCajaEstado();
        this.changed.emit();
      }, (err) => this.showError(err, 'No se pudo cerrar caja'));
    });
  }

  registerIncome(): void {
    if (!this.hasOpenCash) {
      swal.fire('Caja requerida', 'Solo puedes registrar ingresos con caja abierta.', 'warning');
      return;
    }

    const paymentOptions = (this.metodosPago && this.metodosPago.length ? this.metodosPago : [
      { id: 1, nombre: 'EFECTIVO' },
      { id: 2, nombre: 'YAPE' },
      { id: 3, nombre: 'PLIN' },
      { id: 4, nombre: 'TARJETA' },
      { id: 5, nombre: 'TRANSFERENCIA' }
    ]).map(m => `<option value="${m.id || m.mpa_id}">${m.nombre || m.mpa_nombre}</option>`).join('');

    swal.fire({
      title: 'Registrar ingreso a caja',
      html: `
        <div style="display: flex; flex-direction: column; gap: 8px; text-align: left; margin: 10px 0;">
          <label style="font-size: 13px; font-weight: 700; color: #344054;">Monto (S/) *</label>
          <input id="ingresoMonto" type="number" min="0.50" step="0.50" class="swal2-input" style="margin: 0; width: 100%; font-size: 16px; font-weight: 800;" placeholder="0.00">
          
          <label style="font-size: 13px; font-weight: 700; color: #344054; margin-top: 6px;">Concepto / Motivo *</label>
          <input id="ingresoMotivo" class="swal2-input" style="margin: 0; width: 100%; text-transform: uppercase; font-size: 14px;" placeholder="Ej. Aporte de caja, cambio, etc.">
          
          <label style="font-size: 13px; font-weight: 700; color: #344054; margin-top: 6px;">Método de Pago</label>
          <select id="ingresoMetodo" class="swal2-input" style="margin: 0; width: 100%; height: 48px; font-size: 14px; font-weight: 700;">
            ${paymentOptions}
          </select>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: '<i class="ri-add-line"></i> Guardar ingreso',
      confirmButtonColor: '#059669',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        const monto = Number((document.getElementById('ingresoMonto') as HTMLInputElement).value || 0);
        const motivo = ((document.getElementById('ingresoMotivo') as HTMLInputElement).value || '').toUpperCase();
        const mpaId = Number((document.getElementById('ingresoMetodo') as HTMLSelectElement).value || 1);
        if (monto <= 0 || !motivo.trim()) {
          swal.showValidationMessage('Ingresa un monto válido y el motivo del ingreso');
          return false;
        }
        return { monto, motivo, mpaId };
      }
    }).then((res: any) => {
      if (!res.isConfirmed || !res.value) {
        return;
      }
      this.busy = true;
      this.api.salonCajaIngreso({
        p_suc_id: this.sucId,
        p_monto: res.value.monto,
        p_observacion: res.value.motivo,
        p_mpa_id: res.value.mpaId
      }).subscribe(() => {
        this.busy = false;
        swal.fire('Ingreso registrado', `Ingreso a caja: S/ ${Number(res.value.monto).toFixed(2)}`, 'success');
        this.loadCajaEstado();
        this.changed.emit();
      }, (err) => this.showError(err, 'No se pudo registrar el ingreso'));
    });
  }

  registerExpense(): void {
    if (!this.hasOpenCash) {
      swal.fire('Caja requerida', 'Solo puedes registrar egresos con caja abierta.', 'warning');
      return;
    }

    swal.fire({
      title: 'Registrar egreso',
      html: '<input id="egresoMonto" type="number" min="0" class="swal2-input" placeholder="Monto"><input id="egresoMotivo" class="swal2-input" style="text-transform: uppercase;" placeholder="Motivo: compra, movilidad, limpieza...">',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Guardar egreso',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        const monto = Number((document.getElementById('egresoMonto') as HTMLInputElement).value || 0);
        const motivo = ((document.getElementById('egresoMotivo') as HTMLInputElement).value || '').toUpperCase();
        if (monto <= 0 || !motivo.trim()) {
          swal.showValidationMessage('Ingresa monto y motivo del egreso');
          return false;
        }
        return { monto, motivo };
      }
    }).then((res: any) => {
      if (!res.isConfirmed || !res.value) {
        return;
      }
      this.busy = true;
      this.api.salonCajaEgreso({
        p_suc_id: this.sucId,
        p_monto: res.value.monto,
        p_observacion: res.value.motivo
      }).subscribe(() => {
        this.busy = false;
        swal.fire('Egreso registrado', `Salida de caja: S/ ${Number(res.value.monto).toFixed(2)}`, 'success');
        this.loadCajaEstado();
        this.changed.emit();
      }, (err) => this.showError(err, 'No se pudo registrar el egreso'));
    });
  }

  checkout(): void {
    if (!this.hasOpenCash) {
      swal.fire('Caja requerida', 'Primero debes aperturar caja para cobrar ventas.', 'warning');
      return;
    }

    if (!this.cart.length) {
      swal.fire('Venta vacia', 'Agrega servicios o productos antes de cobrar.', 'info');
      return;
    }
    const pagos = this.validPayments();
    if (Number(this.pagosTotal.toFixed(2)) !== Number(this.total.toFixed(2))) {
      swal.fire('Pago incompleto', 'La suma de pagos debe ser igual al total.', 'warning');
      return;
    }

    this.busy = true;
    this.saveClientIfNeeded((cliId: number) => this.api.salonVentaReg({
      p_suc_id: this.sucId,
      p_cli_id: cliId,
      p_pagos: pagos,
      p_items: this.backendItems(this.cart)
    }).subscribe(() => {
      this.busy = false;
      swal.fire('Venta registrada', `Total cobrado: S/ ${this.total.toFixed(2)}`, 'success');
      this.clearCart();
      this.clearClient();
      this.pagos = [{ mpa_id: this.selectedPayment || 1, monto: 0, referencia: '' }];
      this.loadCajaEstado();
      this.changed.emit();
    }, (err) => this.showError(err, 'No se pudo registrar la venta')));
  }

  conformCash(): void {
    if (!this.caja || !this.caja.caj_id) {
      return;
    }
    swal.fire({
      title: 'Conformidad de caja',
      text: 'Confirma que el cierre fisico coincide con el sistema.',
      input: 'textarea',
      inputPlaceholder: 'Observacion opcional',
      showCancelButton: true,
      confirmButtonText: 'Dar conformidad',
      cancelButtonText: 'Cancelar'
    }).then((res: any) => {
      if (!res.isConfirmed) {
        return;
      }
      this.busy = true;
      this.api.salonCajaConformidad({
        p_caj_id: this.caja.caj_id,
        p_observacion: res.value || ''
      }).subscribe(() => {
        this.busy = false;
        swal.fire('Caja conforme', 'Ya se puede aperturar la siguiente caja.', 'success');
        this.loadCajaEstado();
        this.changed.emit();
      }, (err) => this.showError(err, 'No se pudo conformar caja'));
    });
  }

  reopenCash(): void {
    if (!this.caja || !this.caja.caj_id) {
      return;
    }
    swal.fire({
      title: 'Reaperturar caja',
      text: 'Solo use esta opcion si necesita continuar vendiendo despues de haber dado conformidad.',
      input: 'textarea',
      inputPlaceholder: 'Motivo de reapertura',
      showCancelButton: true,
      confirmButtonText: 'Reaperturar',
      cancelButtonText: 'Cancelar'
    }).then((res: any) => {
      if (!res.isConfirmed) {
        return;
      }
      this.busy = true;
      this.api.salonCajaReaperturar({
        p_caj_id: this.caja.caj_id,
        p_observacion: res.value || ''
      }).subscribe(() => {
        this.busy = false;
        swal.fire('Caja reaperturada', 'Ya puedes continuar registrando ventas.', 'success');
        this.loadCajaEstado();
        this.changed.emit();
      }, (err) => this.showError(err, 'No se pudo reaperturar caja'));
    });
  }

  printThermalTicket(): void {
    if (!this.caja || !this.caja.caj_id) {
      return;
    }
    this.api.salonCajaCierreTicket({ p_caj_id: this.caja.caj_id }).subscribe((data: any) => {
      const cab = Array.isArray(data) && Array.isArray(data[0]) ? data[0][0] : {};
      const pagos = Array.isArray(data) && Array.isArray(data[1]) ? data[1] : [];
      const items = Array.isArray(data) && Array.isArray(data[2]) ? data[2] : [];
      const egresos = Array.isArray(data) && Array.isArray(data[3]) ? data[3] : [];
      const manualIngresos = Array.isArray(data) && Array.isArray(data[4]) ? data[4] : [];
      const html = this.buildTicketHtml(cab, pagos, items, egresos, manualIngresos);
      const win = window.open('', '_blank', 'width=360,height=640');
      if (!win) {
        swal.fire('Aviso', 'No se pudo abrir la ventana de impresion.', 'warning');
        return;
      }
      win.document.write(html);
      win.document.close();
      win.focus();
      win.print();
    });
  }

  workerName(workerId: number): string {
    const worker = this.trabajadores.find(item => Number(item.tra_id) === Number(workerId));
    if (!worker) {
      return '';
    }
    return `${worker.per_apepat || ''} ${worker.per_apemat || ''} ${worker.per_nombres || ''}`.trim();
  }

  private showError(err: any, fallback: string): void {
    this.busy = false;
    const message = err && err.error && err.error.error ? err.error.error : fallback;
    swal.fire('Atencion', message, 'error');
  }

  private hasClientData(): boolean {
    return [
      this.cliente.per_numdoc,
      this.cliente.per_apepat,
      this.cliente.per_apemat,
      this.cliente.per_nombres,
      this.cliente.per_celular,
      this.cliente.per_correo
    ].some(value => String(value || '').trim() !== '');
  }

  private saveClientIfNeeded(next: (cliId: number) => void): void {
    if (this.activeAttention && this.activeAttention.cli_id) {
      next(Number(this.activeAttention.cli_id));
      return;
    }

    if (!this.hasClientData()) {
      next(0);
      return;
    }

    const doc = String(this.cliente.per_numdoc || '').trim();
    if (doc) {
      const existing = (this.clientes || []).find((c: any) => String(c.per_numdoc || '').trim() === doc);
      if (existing && existing.cli_id) {
        next(Number(existing.cli_id));
        return;
      }
    }

    this.api.salonClienteGra({
      p_cli_id: 0,
      p_per_tipdoc: this.cliente.per_tipdoc || 'DNI',
      p_per_numdoc: this.cliente.per_numdoc || '',
      p_per_apepat: this.cliente.per_apepat || '',
      p_per_apemat: this.cliente.per_apemat || '',
      p_per_nombres: this.cliente.per_nombres || 'CLIENTE',
      p_per_celular: this.cliente.per_celular || '',
      p_per_correo: this.cliente.per_correo || ''
    }).subscribe((data: any) => {
      const row = Array.isArray(data) ? data[0] : data;
      next(Number(row && row.cli_id ? row.cli_id : 0));
    }, (err) => this.showError(err, 'No se pudo guardar el cliente'));
  }

  private getServiceWorkerIds(): number[] {
    return this.selectedWorkers.length ? this.selectedWorkers.slice() : (this.selectedWorker ? [Number(this.selectedWorker)] : []);
  }

  private addServiceRow(itemId: number, name: string, price: number, workerIds: number[]): void {
    const normalizedIds = workerIds.map(id => Number(id));
    const found = this.cart.find(row =>
      row.tipo === 'SERVICIO' &&
      Number(row.item_id) === Number(itemId) &&
      JSON.stringify(row.tra_ids || [row.tra_id]) === JSON.stringify(normalizedIds)
    );

    if (found) {
      found.cantidad = Number(found.cantidad || 1) + 1;
      this.recalculateItem(found);
    } else {
      this.cart.push({
        tipo: 'SERVICIO',
        item_id: itemId,
        nombre: name,
        tra_id: normalizedIds[0],
        tra_ids: normalizedIds,
        trabajador: normalizedIds.map(id => this.workerName(id)).join(' / '),
        cantidad: 1,
        precio: Number(price || 0),
        total: Number(price || 0)
      });
    }

    this.selectedWorkers = [];
    this.selectedWorker = 0;
  }

  private recalculateItem(item: any): void {
    item.cantidad = Math.max(1, Number(item.cantidad || 1));
    item.precio = Math.max(0, Number(item.precio || 0));
    item.total = Number((Number(item.cantidad) * Number(item.precio)).toFixed(2));
  }

  private backendItems(items: any[]): any[] {
    const payload: any[] = [];

    items.forEach(item => {
      const workerIds = item.tipo === 'SERVICIO' && item.tra_ids && item.tra_ids.length
        ? item.tra_ids.map((id: any) => Number(id))
        : [Number(item.tra_id)];

      if (item.tipo !== 'SERVICIO' || workerIds.length === 1) {
        payload.push({
          tipo: item.tipo,
          item_id: item.item_id,
          ser_id: item.item_id,
          tra_id: workerIds[0],
          cantidad: item.cantidad,
          precio: item.precio
        });
        return;
      }

      const totalLine = Number(item.total || 0);
      const splitPrice = Number((totalLine / workerIds.length).toFixed(2));
      const lastPrice = Number((totalLine - splitPrice * (workerIds.length - 1)).toFixed(2));
      workerIds.forEach((workerId, index) => {
        payload.push({
          tipo: item.tipo,
          item_id: item.item_id,
          ser_id: item.item_id,
          tra_id: workerId,
          cantidad: 1,
          precio: index === workerIds.length - 1 ? lastPrice : splitPrice
        });
      });
    });

    return payload;
  }

  private buildTicketHtml(cab: any, pagos: any[], items: any[], egresos: any[], manualIngresos: any[] = []): string {
    const rows = this.groupTicketItems(items).map(item => `
      <tr>
        <td class="item-cell">
          <strong>${item.item || ''}</strong>
          <small>${item.trabajador || ''}</small>
        </td>
        <td class="qty-cell">${Number(item.vde_cantidad || 0).toFixed(0)}</td>
        <td class="money-cell">S/ ${Number(item.vde_total || 0).toFixed(2)}</td>
      </tr>`).join('');
    const payRows = pagos.map(item => `
      <tr>
        <td>${item.mpa_nombre}</td>
        <td class="money-cell">S/ ${Number(item.total || 0).toFixed(2)}</td>
      </tr>`).join('');
    const incomeRows = (manualIngresos || []).map(item => `
      <tr>
        <td class="expense-cell">
          <strong>${item.cam_observacion || 'Ingreso manual'}</strong>
          <small>${item.cam_fecha || ''} · ${item.mpa_nombre || 'EFECTIVO'}</small>
        </td>
        <td class="money-cell" style="color: #047857; font-weight: 800;">+ S/ ${Number(item.cam_monto || 0).toFixed(2)}</td>
      </tr>`).join('');
    const expenseRows = egresos.map(item => `
      <tr>
        <td class="expense-cell">
          <strong>${item.cam_observacion || 'Egreso'}</strong>
          <small>${item.cam_fecha || ''}</small>
        </td>
        <td class="money-cell" style="color: #b91c1c;">- S/ ${Number(item.cam_monto || 0).toFixed(2)}</td>
      </tr>`).join('');
    return `
      <html><head><title>Cierre de caja</title>
      <style>
        @page { size: 80mm auto; margin: 3mm; }
        * { box-sizing: border-box; }
        body {
          width: 74mm;
          margin: 0 auto;
          color: #111827;
          background: #fff;
          font-family: "Arial Narrow", Arial, sans-serif;
          font-size: 9px;
          line-height: 1.12;
        }
        .ticket { padding: 2mm 1mm 4mm; }
        .brand {
          text-align: center;
          padding-bottom: 5px;
          border-bottom: 1px solid #111827;
        }
        .brand h1 {
          margin: 0;
          font-size: 14px;
          letter-spacing: 0;
          line-height: 1;
        }
        .brand p { margin: 2px 0 0; font-size: 9px; }
        .meta {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 3px 8px;
          padding: 6px 0;
          border-bottom: 1px dashed #9ca3af;
          font-size: 9.5px;
        }
        .meta span:nth-child(even) { text-align: right; font-weight: 700; }
        .section-title {
          margin: 7px 0 3px;
          padding: 3px 0;
          color: #111827;
          background: #f3f4f6;
          border-top: 1px solid #d1d5db;
          border-bottom: 1px solid #d1d5db;
          text-align: center;
          font-size: 9px;
          font-weight: 800;
          text-transform: uppercase;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
        }
        th {
          padding: 3px 0;
          border-bottom: 1px solid #111827;
          font-size: 8px;
          text-align: left;
          text-transform: uppercase;
        }
        td {
          padding: 3px 0;
          border-bottom: 1px dashed #d1d5db;
          vertical-align: top;
        }
        .payments td:first-child { width: 56%; font-weight: 700; }
        .expense-cell strong { display: block; font-size: 8.5px; }
        .expense-cell small {
          display: block;
          color: #6b7280;
          font-size: 6.8px;
          font-weight: 700;
          margin-top: 1px;
        }
        .items th:nth-child(1), .item-cell { width: 58%; }
        .items th:nth-child(2), .qty-cell { width: 13%; text-align: center; }
        .items th:nth-child(3), .money-cell { width: 29%; text-align: right; }
        .item-cell strong {
          display: block;
          font-size: 8.5px;
          font-weight: 700;
          word-break: break-word;
        }
        .item-cell small {
          display: block;
          margin-top: 1px;
          color: #4b5563;
          font-size: 6.8px;
          font-weight: 700;
          text-transform: uppercase;
        }
        .payments td {
          font-size: 8.8px;
          font-weight: 700;
        }
        .payments .money-cell,
        .items .money-cell,
        .items .qty-cell {
          font-size: 9px;
          font-weight: 700;
        }
        .total-box {
          margin-top: 7px;
          padding: 6px 0;
          border-top: 1px solid #111827;
          border-bottom: 1px solid #111827;
          text-align: right;
          font-size: 10px;
          font-weight: 900;
        }
        .signature {
          margin-top: 14px;
          text-align: center;
          font-size: 9px;
        }
        .signature-line {
          width: 48mm;
          margin: 22px auto 4px;
          border-top: 1px solid #111827;
        }
        .footer {
          margin-top: 8px;
          padding-top: 5px;
          border-top: 1px dashed #9ca3af;
          text-align: center;
          font-size: 8px;
          color: #4b5563;
        }
      </style></head><body>
      <main class="ticket">
        <header class="brand">
          <h1>CIERRE DE CAJA</h1>
          <p>${cab.suc_nombre || 'Principal'} · ${cab.caj_fecha || ''}</p>
        </header>

        <section class="meta">
          <span>Apertura</span><span>S/ ${Number(cab.caj_apertura || 0).toFixed(2)}</span>
          <span>Ingresos</span><span>S/ ${Number(cab.ingresos || 0).toFixed(2)}</span>
          <span>Egresos</span><span>S/ ${Number(cab.egresos || 0).toFixed(2)}</span>
          <span>Cierre calculado</span><span>S/ ${Number(cab.saldo_calculado || cab.caj_cierre || 0).toFixed(2)}</span>
        </section>

        <div class="section-title">Resumen de pagos</div>
        <table class="payments"><tbody>${payRows}</tbody></table>

        <div class="section-title">Servicios y ventas</div>
        <table class="items">
          <thead><tr><th>Item</th><th>Cant</th><th>Total</th></tr></thead>
          <tbody>${rows || '<tr><td colspan="3" style="text-align:center;color:#6b7280;padding:6px 0;">Sin ventas</td></tr>'}</tbody>
        </table>

        ${incomeRows ? `<div class="section-title" style="background:#ecfdf5;border-color:#a7f3d0;color:#065f46;">Ingresos directos / Aportes</div><table class="payments"><tbody>${incomeRows}</tbody></table>` : ''}

        ${expenseRows ? `<div class="section-title" style="background:#fff1f2;border-color:#fecdd3;color:#9f1239;">Detalle de egresos</div><table class="payments"><tbody>${expenseRows}</tbody></table>` : ''}

        <div class="total-box">TOTAL CAJA S/ ${Number(cab.saldo_calculado || cab.caj_cierre || 0).toFixed(2)}</div>

        <section class="signature">
          <div class="signature-line"></div>
          Firma de conformidad
        </section>
        <div class="footer">Documento interno de cierre de caja</div>
      </main>
      </body></html>`;
  }

  private groupTicketItems(items: any[]): any[] {
    const grouped: any[] = [];
    const index: {[key: string]: any} = {};

    (items || []).forEach((item: any) => {
      const key = [
        item.ven_id || '',
        item.tip_nombre || '',
        String(item.item || '').trim().toUpperCase()
      ].join('|');

      if (!index[key]) {
        index[key] = Object.assign({}, item, {
          trabajador: '',
          vde_cantidad: item.tip_nombre === 'SERVICIO' ? 1 : Number(item.vde_cantidad || 0),
          vde_total: 0
        });
        grouped.push(index[key]);
      } else if (item.tip_nombre !== 'SERVICIO') {
        index[key].vde_cantidad = Number(index[key].vde_cantidad || 0) + Number(item.vde_cantidad || 0);
      }

      index[key].vde_total = Number(index[key].vde_total || 0) + Number(item.vde_total || 0);

      const workerName = String(item.trabajador || '').trim();
      if (workerName) {
        const workers = String(index[key].trabajador || '').split(' / ').filter(Boolean);
        if (!workers.some(worker => worker.toUpperCase() === workerName.toUpperCase())) {
          workers.push(workerName);
          index[key].trabajador = workers.join(' / ');
        }
      }
    });

    return grouped;
  }

  private toDateInput(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }
}
